import { useState, useCallback, useEffect, useRef } from 'react';
import { phase3API } from '../services/api';
import { fetchWithAuth } from '../config/api';
import '../css/Phase2PDFUpload.css';
import { normalizeProgressMessage } from '../utils/progressMessage';
import { MOCK_MODE } from '../config/mock';
import EditableLabel from './EditableLabel';
import { useLabels } from '../config/labelsContext';

interface Phase2PDFUploadProps {
  sessionId: string;
  onComplete: (data: unknown) => void;
  setIsLoading: (loading: boolean) => void;
  setLoadingMessage: (message: string) => void;
  showAlert?: (message: string, title?: string) => void;
}

const Phase2PDFUpload = ({
  sessionId,
  onComplete,
  setIsLoading,
  setLoadingMessage,
  showAlert,
}: Phase2PDFUploadProps) => {
  const { labels } = useLabels();
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const classifyInFlightRef = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const classifyDoneForSessionRef = useRef<string | null>(null);
  const analysisJobIdRef = useRef<string | null>(null);

  const formatProgressMessage = (
    statusData: Record<string, unknown> | null | undefined,
    fallback: string
  ) => {
    const msgRaw = statusData?.progress_message;
    const base = typeof msgRaw === 'string' && msgRaw ? msgRaw : fallback;
    const msg = normalizeProgressMessage(base);

    const curRaw = statusData?.progress_current;
    const cur = typeof curRaw === 'number' ? curRaw : Number(curRaw ?? 0);

    const totalRaw = statusData?.progress_total;
    const total = totalRaw != null ? Number(totalRaw) : null;

    // Avoid showing "完了" while still in progress.
    if (total && cur < total && /完了/.test(msg)) {
      return labels.phase2pdf.analyzeLoadingMessage;
    }
    return msg;
  };

  const alertUI = (message: string) => {
    if (showAlert) return showAlert(message, labels.modals.noticeTitle);
    window.alert(message);
  };

  const startPollingAnalysisStatus = () => {
    if (!sessionId) return;
    // Clear any previous polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;

    pollIntervalRef.current = setInterval(async () => {
      try {
        const statusRes = await fetchWithAuth(
          `/api/phase2/check-status?session_id=${encodeURIComponent(sessionId)}`
        );

        if (!statusRes.ok) {
          consecutiveErrors++;
          // eslint-disable-next-line no-console
          console.warn(`check-status failed (${statusRes.status}), attempt ${consecutiveErrors}`);

          // Stop polling after too many consecutive errors
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setAnalyzing(false);
            setIsLoading(false);
            alertUI(`${labels.phase2pdf.sessionStatusError} (HTTP ${statusRes.status})`);
          }
          return;
        }

        // Reset error counter on success
        consecutiveErrors = 0;

        const statusData = (await statusRes.json()) as Record<string, unknown>;
        // Prefer job progress when a background job is running.
        const asyncTaskIdRaw = statusData?.async_task_id;
        const asyncTaskId = typeof asyncTaskIdRaw === 'string' ? asyncTaskIdRaw.trim() : '';
        const jobId = asyncTaskId || analysisJobIdRef.current || '';
        if (jobId) {
          try {
            const jobRes = await fetchWithAuth(`/api/jobs/${encodeURIComponent(jobId)}`);
            if (jobRes.ok) {
              const job = (await jobRes.json()) as Record<string, unknown>;
              const jobStatus = typeof job.status === 'string' ? job.status : '';
              const msgRaw = job.progress_message;
              const msg =
                typeof msgRaw === 'string' && msgRaw.trim() ? normalizeProgressMessage(msgRaw) : '';
              const cur = Number(job.progress_current ?? 0);
              const total = job.progress_total != null ? Number(job.progress_total) : null;
              if (jobStatus && jobStatus !== 'succeeded') {
                // If failed/cancelled, stop and surface the reason.
                if (jobStatus === 'failed' || jobStatus === 'cancelled') {
                  const errRaw = job.error_message;
                  const err =
                    typeof errRaw === 'string' && errRaw.trim()
                      ? errRaw
                      : labels.phase2pdf.jobFailedError;
                  if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                  }
                  setAnalyzing(false);
                  setIsLoading(false);
                  alertUI(err);
                  return;
                }
                // Running/queued: keep polling and show job progress.
                if (total != null && Number.isFinite(total) && total > 0) {
                  setLoadingMessage(
                    `${msg || labels.phase2pdf.analyzeLoadingMessage} (${cur}/${total})`
                  );
                } else {
                  setLoadingMessage(msg || labels.phase2pdf.analyzeLoadingMessage);
                }
                return;
              }
            }
          } catch {
            // best-effort; fall back to session progress below
          }
        }

        setLoadingMessage(
          formatProgressMessage(statusData, labels.phase2pdf.analyzeLoadingMessage)
        );

        const curRaw = statusData?.progress_current;
        const cur = typeof curRaw === 'number' ? curRaw : Number(curRaw ?? 0);
        const totalRaw = statusData?.progress_total;
        const total = totalRaw != null ? Number(totalRaw) : null;
        const progressComplete =
          total != null && Number.isFinite(total) && total > 0 && cur >= total;
        const currentStep = Number(statusData?.current_step ?? 0);

        // If classification already finished (step>=8), do NOT try to call phase3/session here.
        // Instead, re-run the idempotent classification endpoint once to fetch the groups and proceed.
        if (currentStep >= 8) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setLoadingMessage(labels.phase2pdf.classifyLoadingMessage);
          await handleClassification();
          return;
        }

        // Move forward ONLY when the background analysis job succeeded.
        // (Session step/progress can flip early; job status is the source of truth.)
        const readySignal = statusData.is_complete === true || currentStep >= 5 || progressComplete;
        if (readySignal && currentStep < 8) {
          const asyncTaskIdRaw2 = statusData?.async_task_id;
          const asyncTaskId2 = typeof asyncTaskIdRaw2 === 'string' ? asyncTaskIdRaw2.trim() : '';
          const jobId2 = asyncTaskId2 || analysisJobIdRef.current || '';
          if (jobId2) {
            try {
              const jobRes = await fetchWithAuth(`/api/jobs/${encodeURIComponent(jobId2)}`);
              if (jobRes.ok) {
                const job = (await jobRes.json()) as Record<string, unknown>;
                const jobStatus = typeof job.status === 'string' ? job.status : '';
                if (jobStatus !== 'succeeded') {
                  // Not done yet; keep polling.
                  return;
                }
              }
            } catch {
              // If job status cannot be fetched, fall back to old behavior.
            }
          }

          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setLoadingMessage(labels.phase2pdf.classifyLoadingMessage);
          await handleClassification();
        }
      } catch (e) {
        consecutiveErrors++;
        // eslint-disable-next-line no-console
        console.warn('Polling error:', e);

        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setAnalyzing(false);
          setIsLoading(false);
          alertUI(labels.phase2pdf.networkError);
        }
      }
    }, 5000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Restore session state on mount
  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;

    const checkSessionStatus = async () => {
      try {
        // 1) If Phase2-A is currently running, resume the loading modal + polling.
        const statusRes = await fetchWithAuth(
          `/api/phase2/check-status?session_id=${encodeURIComponent(sessionId)}`
        );
        if (statusRes.ok) {
          const statusData = (await statusRes.json()) as Record<string, unknown>;
          if (cancelled) return;

          const curRaw = statusData?.progress_current;
          const cur = typeof curRaw === 'number' ? curRaw : Number(curRaw ?? 0);
          const totalRaw = statusData?.progress_total;
          const total = totalRaw != null ? Number(totalRaw) : null;
          const progressComplete =
            total != null && Number.isFinite(total) && total > 0 && cur >= total;
          const currentStep = Number(statusData?.current_step ?? 0);
          const asyncTaskIdRaw = statusData?.async_task_id;
          const asyncTaskId = typeof asyncTaskIdRaw === 'string' ? asyncTaskIdRaw.trim() : '';
          if (asyncTaskId) {
            analysisJobIdRef.current = asyncTaskId;
          }

          // If classification already finished (step>=8), re-run the idempotent classification endpoint
          // once to fetch the groups and proceed.
          if (currentStep >= 8) {
            setAnalyzing(true);
            setIsLoading(true);
            setLoadingMessage(labels.phase2pdf.classifyLoadingMessage);
            await handleClassification();
            return;
          }

          // Proceed to classification ONLY when the analysis job succeeded.
          if (statusData.is_complete === true || currentStep >= 5 || progressComplete) {
            const jobId = asyncTaskId || analysisJobIdRef.current || '';
            if (jobId) {
              try {
                const jobRes = await fetchWithAuth(`/api/jobs/${encodeURIComponent(jobId)}`);
                if (jobRes.ok) {
                  const job = (await jobRes.json()) as Record<string, unknown>;
                  const jobStatus = typeof job.status === 'string' ? job.status : '';
                  if (jobStatus !== 'succeeded') {
                    // Still running/queued; resume polling instead of advancing early.
                    setAnalyzing(true);
                    setIsLoading(true);
                    setLoadingMessage(
                      formatProgressMessage(statusData, labels.phase2pdf.analyzeLoadingMessage)
                    );
                    startPollingAnalysisStatus();
                    return;
                  }
                }
              } catch {
                // fall through to old behavior
              }
            }
            setAnalyzing(true);
            setIsLoading(true);
            setLoadingMessage(labels.phase2pdf.classifyLoadingMessage);
            await handleClassification();
            return;
          }

          // If analysis is still running (status is analyzing_pdfs, or async_task_id is set, or progress_total set), resume polling.
          const status = statusData?.status as string | undefined;
          const asyncTaskId2 = statusData?.async_task_id as string | undefined;
          if (status === 'analyzing_pdfs' || asyncTaskId2 || statusData.progress_total != null) {
            setAnalyzing(true);
            setIsLoading(true);
            setLoadingMessage(
              formatProgressMessage(statusData, labels.phase2pdf.analyzeLoadingMessage)
            );
            startPollingAnalysisStatus();
            return;
          }

          // If analysis finished but classification not done yet, resume classification.
          if (statusData.is_complete === true && Number(statusData.current_step ?? 0) < 8) {
            setAnalyzing(true);
            setIsLoading(true);
            setLoadingMessage(labels.phase2pdf.classifyLoadingMessage);
            await handleClassification();
            return;
          }
        }

        // 2) Otherwise, if the session already progressed (step >= 8), move forward.
        const sessionData = await phase3API.getSession(sessionId);
        if (cancelled) return;

        // If files are already uploaded (step >= 5)
        if (sessionData && sessionData.current_step >= 5) {
          // If already classified (step >= 8), notify completion (only once)
          if (sessionData.current_step >= 8) {
            // Ensure the global loading modal is closed when we jump forward.
            setAnalyzing(false);
            setIsLoading(false);
            setLoadingMessage('');
            onComplete({
              classification_groups: sessionData.classification_groups || [],
              unclassified_keywords: sessionData.unclassified_keywords || [],
              has_unclassified: (sessionData.unclassified_keywords || []).length > 0,
              next_step:
                (sessionData.unclassified_keywords || []).length > 0
                  ? 'research_unclassified'
                  : 'generate_formulas',
            });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(labels.phase2pdf.sessionRestoreError + ':', error);
      }
    };

    checkSessionStatus();

    return () => {
      cancelled = true;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    // We intentionally do not include some function deps to avoid restarting
    // long polling/classification flows due to referential changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, onComplete]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (file) => file.type === 'application/pdf'
    );
    setFiles((prev) => [...prev, ...droppedFiles]);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...selectedFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      alertUI('PDFをアップロードしてください');
      return;
    }

    if (!sessionId || sessionId.trim() === '') {
      alertUI(labels.phase2pdf.noSessionAlert);
      return;
    }

    if (MOCK_MODE) {
      setUploading(false);
      setAnalyzing(false);
      setIsLoading(false);
      setLoadingMessage('');
      onComplete({ mock: true, file_count: files.length });
      return;
    }

    // Reset per-session guards so re-uploads can re-run classification.
    classifyDoneForSessionRef.current = null;

    setUploading(true);
    setIsLoading(true);
    setLoadingMessage(labels.phase2pdf.uploadLoadingMessage);

    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      files.forEach((file) => {
        formData.append('files', file);
      });

      // Set timeout for large file uploads (60 minutes)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3600000); // 60 minutes

      const response = await fetchWithAuth(`/api/phase2/upload-pdfs`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Backend/Proxy might return HTML (e.g. 413/502). Don't blindly parse JSON.
        const contentType = response.headers.get('content-type') || '';
        let detail = `${labels.phase2pdf.uploadError} (HTTP ${response.status})`;
        try {
          if (contentType.includes('application/json')) {
            const errorData = await response.json();
            detail = errorData?.detail || detail;
          } else {
            const text = await response.text();
            // show a short hint without dumping full HTML
            const snippet = text.replace(/\s+/g, ' ').slice(0, 120);
            if (response.status === 413) {
              detail = labels.phase2pdf.uploadFileTooLarge;
            } else if (snippet) {
              detail = `${detail}: ${snippet}`;
            }
          }
        } catch {
          // ignore parsing errors
        }
        throw new Error(detail);
      }

      const data = (await response.json()) as Record<string, unknown>;
      const jobIdRaw = data?.job_id;
      const jobId = typeof jobIdRaw === 'string' ? jobIdRaw.trim() : '';
      if (jobId) {
        analysisJobIdRef.current = jobId;
      }
      setUploading(false);
      setAnalyzing(true);
      setLoadingMessage(labels.phase2pdf.analyzeLoadingMessage);

      // Start polling for completion
      startPollingAnalysisStatus();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Upload failed:', error);
      let errorMessage = labels.phase2pdf.uploadError;

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = labels.phase2pdf.uploadTimeout;
        } else {
          errorMessage = error.message;
        }
      }

      alertUI(errorMessage);
      setUploading(false);
      setIsLoading(false);
    }
  };

  const handleClassification = async () => {
    // sessionIdが無いなら何もしない
    if (!sessionId || sessionId.trim() === '') return;

    // 同一sessionで既に完了しているなら再実行しない（保険）
    if (classifyDoneForSessionRef.current === sessionId) return;

    // 同時実行ガード：2本目以降は通さない
    if (classifyInFlightRef.current) return;
    classifyInFlightRef.current = true;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3600000); // 60 minutes

    try {
      const response = await fetchWithAuth(
        `/api/phase2/classify-and-extract?session_id=${encodeURIComponent(sessionId)}`,
        {
          method: 'POST',
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        // 409（duplicate）とかもここに来る
        throw new Error(labels.phase2pdf.classifyError);
      }

      const data = await response.json();

      // 成功したら「このsessionは分類済み」とマーク
      classifyDoneForSessionRef.current = sessionId;

      // Intentionally do not show a modal for unclassified keywords here.
      // The next phase can still handle/display the information as needed.

      setAnalyzing(false);
      setIsLoading(false);
      onComplete(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Classification failed:', error);

      let errorMessage = labels.phase2pdf.classifyError;
      if (error instanceof Error && error.name === 'AbortError') {
        errorMessage = labels.phase2pdf.classifyTimeout;
      } else if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }

      alertUI(errorMessage);
      setAnalyzing(false);
      setIsLoading(false);
    } finally {
      clearTimeout(timeoutId);
      controller.abort(); // 念のため（不要だが安全）
      classifyInFlightRef.current = false;
    }
  };

  return (
    <div className="phase2-pdf-upload">
      <div className="phase-card">
        <h2 className="phase-title">
          <span className="phase-title-step">
            <EditableLabel labelKey="phase2pdf.stepLabel" defaultValue="Step1:" />
          </span>
          <span className="phase-title-text">
            <EditableLabel labelKey="phase2pdf.title" defaultValue="分野の指定" />
          </span>
        </h2>
        <div className="workflow-step-description phase-description-banner">
          <EditableLabel
            labelKey="phase2pdf.description"
            defaultValue="特許マップの分野を指定するため、特許例入力（PDF形式）を完了してください"
          />
        </div>

        <div
          className={`dropzone ${dragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="dropzone-content">
            <div className="upload-icon">📄</div>
            <p className="dropzone-text">
              <EditableLabel
                labelKey="phase2pdf.dropzoneText"
                defaultValue="PDFファイルをここにドラッグ&ドロップ"
              />
            </p>
            <p className="dropzone-or">
              <EditableLabel labelKey="phase2pdf.dropzoneOr" defaultValue="または" />
            </p>
            <label htmlFor="file-input" className="file-select-btn">
              <EditableLabel labelKey="phase2pdf.fileSelectButton" defaultValue="ファイルを選択" />
            </label>
            <input
              id="file-input"
              type="file"
              multiple
              accept=".pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {files.length > 0 && (
          <div className="files-list">
            <h3>
              <EditableLabel
                labelKey="phase2pdf.selectedFilesTitle"
                defaultValue="選択されたファイル"
              />{' '}
              ({files.length}件)
            </h3>
            <div className="files">
              {files.map((file, index) => (
                <div key={index} className="file-item">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                  <button
                    onClick={() => removeFile(index)}
                    className="remove-btn"
                    disabled={uploading || analyzing}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!sessionId || sessionId.trim() === '' ? (
          <div className="error-message" style={{ color: 'red', margin: '10px 0' }}>
            <EditableLabel
              labelKey="phase2pdf.noSessionWarning"
              defaultValue="警告: セッションIDがありません。先にPhase 1を完了してください。"
            />
          </div>
        ) : null}

        <div className="upload-btn-wrapper">
          <button
            onClick={handleUpload}
            className="upload-btn btn-primary"
            disabled={uploading || analyzing}
          >
            {uploading ? (
              <>
                <span className="spinner"></span>
                <EditableLabel
                  labelKey="phase2pdf.uploadingButton"
                  defaultValue="アップロード中..."
                />
              </>
            ) : analyzing ? (
              <>
                <span className="spinner"></span>
                <EditableLabel labelKey="phase2pdf.analyzingButton" defaultValue="分析中..." />
              </>
            ) : (
              <EditableLabel
                labelKey="phase2pdf.uploadButton"
                defaultValue="アップロードして解析開始"
              />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Phase2PDFUpload;
